import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, X } from "lucide-react";

export default function Quiz() {
  const { id } = useParams();
  const { t, tField, lang } = useI18n();
  const nav = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/diagnostics/${id}`, { params: { lang } })
      .then((res) => setQuiz(res.data))
      .catch((e) => {
        toast.error(formatApiError(e));
        nav("/catalog");
      });
  }, [id, nav, lang]);

  if (!quiz) {
    return <div className="py-32 text-center text-slate-400">Loading…</div>;
  }

  const total = quiz.questions.length;
  const q = quiz.questions[idx];
  const selected = answers[q.id];
  const progress = Math.round(((idx + (selected ? 1 : 0)) / total) * 100);

  const handleNext = () => {
    if (!selected) {
      toast.error(t("quiz.required"));
      return;
    }
    if (idx < total - 1) setIdx(idx + 1);
  };

  const handlePrev = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  const handleSubmit = async () => {
    if (!selected) {
      toast.error(t("quiz.required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        lang,
        answers: quiz.questions.map((qq) => ({
          question_id: qq.id,
          option_id: answers[qq.id],
        })),
      };
      const { data } = await api.post(`/diagnostics/${id}/submit`, payload);
      nav(`/result/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#0A0E1A]">
      {/* Top bar */}
      <div className="sticky top-16 z-30 bg-[#0A0E1A]/95 backdrop-blur-xl border-b border-[#1E2A3F]">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
              {tField(quiz.category_label)} · <span className="text-white">{tField(quiz.title)}</span>
            </div>
            <button
              onClick={() => nav("/catalog")}
              data-testid="quiz-exit-button"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] hover:text-[#FF3B30]"
            >
              <X className="h-3 w-3" strokeWidth={2} />
              {t("quiz.exit")}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-[#1E2A3F] overflow-hidden">
              <div
                className="h-full bg-[#3B82F6] progress-fill"
                style={{ width: `${progress}%`, boxShadow: "0 0 8px rgba(59,130,246,0.6)" }}
                data-testid="quiz-progress-bar"
              />
            </div>
            <div className="font-mono text-sm text-[#3B82F6] min-w-[70px] text-right tabular-nums">
              {String(idx + 1).padStart(2, "0")} {t("quiz.of")} {String(total).padStart(2, "0")}
            </div>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="mx-auto max-w-3xl px-6 py-12 lg:py-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-3">
          // {t("quiz.question")} {idx + 1}
        </div>
        <h2
          data-testid="quiz-question-text"
          className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-snug text-white uppercase"
        >
          {tField(q.text)}
        </h2>

        <div className="mt-10 space-y-3">
          {q.options.map((o, i) => {
            const active = selected === o.id;
            return (
              <button
                key={o.id}
                data-testid={`quiz-option-${o.id}`}
                onClick={() => setAnswers({ ...answers, [q.id]: o.id })}
                className={`w-full flex items-center gap-4 border p-5 text-left transition-all ${
                  active
                    ? "border-[#3B82F6] bg-[#3B82F6]/8 text-white"
                    : "border-[#1E2A3F] bg-[#0F1525] text-white hover:border-[#3B82F6]/60"
                }`}
              >
                <span
                  className={`h-8 w-8 flex items-center justify-center font-mono font-bold text-sm ${
                    active ? "bg-[#3B82F6] text-black" : "bg-[#161E33] text-[#94A3B8]"
                  }`}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="flex-1 text-base leading-snug">{tField(o.text)}</span>
                {active && <CheckCircle2 className="h-5 w-5 text-[#3B82F6]" strokeWidth={2} />}
              </button>
            );
          })}
        </div>

        {/* Nav */}
        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={idx === 0}
            data-testid="quiz-prev-button"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#1E2A3F] text-[#94A3B8] font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:border-[#3B82F6] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            {t("quiz.prev")}
          </button>
          {idx < total - 1 ? (
            <button
              onClick={handleNext}
              data-testid="quiz-next-button"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA]"
            >
              {t("quiz.next")}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="quiz-submit-button"
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] neon-glow-soft disabled:opacity-60"
            >
              {submitting ? t("quiz.submitting") : t("quiz.submit")}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
